import React, { useState, useEffect, useMemo } from "react";
import classNames from "classnames";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Badge,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import { useCreateFlag, useGetFlags } from "../../../api/flags/service";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { getSelectStyles } from "../../../utils/selectStyles";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import { useTranslation } from "react-i18next";
import Select from "components/Select";

const getPriorityOptions = (t) => [
  { value: "High", label: t("flagTab.high") },
  { value: "Medium", label: t("flagTab.medium") },
  { value: "Low", label: t("flagTab.low") },
  { value: "Informational", label: t("flagTab.informational") },
];

/** 1-based page numbers; returns { type: 'page'|'ellipsis', page?, key? } */
function buildFlagPaginationItems(currentPage, pageCount) {
  const total = Math.max(1, pageCount || 1);
  const current = Math.min(Math.max(1, currentPage || 1), total);
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => ({ type: "page", page: i + 1 }));
  }
  const set = new Set([1, total]);
  for (let p = current - 1; p <= current + 1; p += 1) {
    if (p >= 1 && p <= total) set.add(p);
  }
  const sorted = [...set].sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i += 1) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      out.push({ type: "ellipsis", key: `e-${sorted[i - 1]}-${sorted[i]}` });
    }
    out.push({ type: "page", page: sorted[i] });
  }
  return out;
}

const FlagTab = ({
  permission,
  flags,
  id,
  activeColor = "secondary",
  module,
  update,
  isTheme2 = false,
  compact = false,
}) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const [isLightTheme, setIsLightTheme] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains("white-content"));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  const customSelectStyles = useMemo(
    () => getSelectStyles(activeColorStyle, isLightTheme),
    [activeColorStyle, isLightTheme]
  );
  const selectStyles = useMemo(
    () =>
      isTheme2
        ? getTheme2SearchFilterSingleSelectStyles(activeColorStyle, true)
        : customSelectStyles,
    [isTheme2, activeColorStyle, customSelectStyles]
  );
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".flag-tab.flag-tab--theme2",
            fieldsSelector: ".flag-tab-fields",
          })
        : "",
    [isTheme2]
  );
  const inputStyle = isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined;

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = compact ? 8 : 12;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Load full module catalog once; filter + paginate client-side so
  // priority/search never leave uneven page sizes.
  const { data: flagsData, isLoading } = useGetFlags(module, {
    page: 1,
    pageSize: 500,
  });

  const cardHoverStyle = {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
  };

  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    priority: "Low",
    module: module,
  });
  const [formErrors, setFormErrors] = useState({});
  const [newSelectedFlags, setNewSelectedFlags] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState("");
  const handleCancelAdd = () => {
    setShowAddForm(false);
    setForm({
      name: "",
      description: "",
      priority: "Low",
      module: module,
    });
    setFormErrors({});
  };
  const createFlagMutation = useCreateFlag({ onClose: () => handleCancelAdd() });

  useEffect(() => {
    if (flags) {
      setNewSelectedFlags(flags?.map((f) => f?.id));
    }
  }, [flags]);

  const handleAddFlag = () => {
    setShowAddForm(true);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("flagTab.flagNameRequired");
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFlagToggle = (flagId) => {
    setNewSelectedFlags((prev) =>
      prev.includes(flagId) ? prev.filter((id) => id !== flagId) : [...prev, flagId]
    );
  };

  const handleAssignFlags = async () => {
    if (!id) {
      setAssignError(t("flagTab.inventoryIdNotFound"));
      return;
    }
    setAssignLoading(true);
    setAssignError(null);
    try {
      await update.mutateAsync({ id, data: { flags: newSelectedFlags } });
      setAssignLoading(false);
      setNewSelectedFlags([]);
    } catch (err) {
      setAssignLoading(false);
      setAssignError(t("flagTab.flagAssignmentFailed"));
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await createFlagMutation.mutateAsync({
        name: form.name,
        description: form.description,
        module: module,
        priority: form.priority,
      });
      handleCancelAdd();
    } catch (error) {
      console.error("Error creating flag:", error);
    }
  };

  const getPriorityColor = (priority) => {
    const colorMap = {
      High: "danger",
      Medium: "warning",
      Low: "info",
      Informational: "secondary",
    };
    return colorMap[priority] || "secondary";
  };

  const filteredFlags = useMemo(() => {
    const all = flagsData?.results || [];
    const q = debouncedSearch.toLowerCase();
    return all.filter((flag) => {
      if (priorityFilter && flag.priority !== priorityFilter) return false;
      if (!q) return true;
      const name = String(flag.name || "").toLowerCase();
      const description = String(flag.description || "").toLowerCase();
      return name.includes(q) || description.includes(q);
    });
  }, [flagsData?.results, debouncedSearch, priorityFilter]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, priorityFilter, pageSize]);

  const totalFlags = filteredFlags.length;
  const pageCount = Math.max(1, Math.ceil(totalFlags / pageSize));
  const safePage = Math.min(page, pageCount);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const pagedFlags = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredFlags.slice(start, start + pageSize);
  }, [filteredFlags, safePage, pageSize]);

  const paginationItems = useMemo(
    () => buildFlagPaginationItems(safePage, pageCount),
    [safePage, pageCount]
  );

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
  };

  const handlePriorityChange = (option) => {
    setPriorityFilter(option?.value || "");
  };

  return (
    <div
      className={classNames("flag-tab", {
        "flag-tab--theme2": isTheme2,
        "flag-tab--compact": compact,
      })}
      style={compact ? { overflowX: "hidden", maxWidth: "100%" } : undefined}
    >
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      {compact ? (
        <style>{`
          .flag-tab--compact .flag-card {
            margin-bottom: 0;
            box-shadow: none !important;
            transform: none !important;
          }
          .flag-tab--compact .flag-card .card-body {
            padding: 0.75rem 0.85rem;
          }
          .flag-tab--compact .flag-tab-list {
            overflow-x: hidden;
            max-width: 100%;
          }
        `}</style>
      ) : null}
      <style>{`
        .flag-tab-pagination {
          display: flex;
          justify-content: center;
          margin: 0;
        }
        .flag-tab-pagination .pagination {
          margin: 0;
          flex-wrap: wrap;
          justify-content: center;
          gap: 4px;
        }
        .flag-tab-pagination .page-item .page-link {
          border-radius: 0 !important;
          border: 1px solid transparent !important;
          min-width: 34px;
          height: 34px;
          padding: 0 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          font-weight: 600;
          line-height: 1;
        }
        .flag-tab-pagination--light .page-item:not(.active):not(.disabled) .page-link {
          color: #1d253b !important;
          background-color: rgba(154, 154, 154, 0.12) !important;
        }
        .flag-tab-pagination--light .page-item:not(.active):not(.disabled) .page-link:hover,
        .flag-tab-pagination--light .page-item:not(.active):not(.disabled) .page-link:focus {
          color: #1d253b !important;
          background-color: rgba(154, 154, 154, 0.22) !important;
        }
        .flag-tab-pagination--dark .page-item:not(.active):not(.disabled) .page-link {
          color: rgba(255, 255, 255, 0.88) !important;
          background-color: rgba(255, 255, 255, 0.06) !important;
        }
        .flag-tab-pagination--dark .page-item:not(.active):not(.disabled) .page-link:hover,
        .flag-tab-pagination--dark .page-item:not(.active):not(.disabled) .page-link:focus {
          color: #ffffff !important;
          background-color: rgba(255, 255, 255, 0.12) !important;
        }
        .flag-tab-pagination .page-item.disabled .page-link {
          opacity: 0.35;
          cursor: default;
        }
        .flag-tab-footer {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
        }
        .flag-tab--compact.flag-tab .flag-tab-footer--light {
          border-top-color: rgba(0, 0, 0, 0.08);
        }
      `}</style>
      <div className={classNames({ "flag-tab-fields": isTheme2 })}>
        {/* Header Section */}
        <div className="row mb-3 align-items-end w-100 mx-0">
          <div className={classNames(compact ? "col-12 col-md-5 px-0 pr-md-2 mb-2 mb-md-0" : "col-md-4")}>
            <FormGroup className="mb-0">
              <Label>{t("flagTab.search")}</Label>
              <Input
                type="text"
                className="form-control"
                style={inputStyle}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("flagTab.searchPlaceholder")}
              />
            </FormGroup>
          </div>
          <div className={classNames(compact ? "col-12 col-md-4 px-0 pr-md-2 mb-2 mb-md-0" : "col-md-4")}>
            <FormGroup className="mb-0">
              <Label>{t("flagTab.flagPriorityFilter")}</Label>
              <Select
                styles={selectStyles}
                className="react-select"
                placeholder={t("flagTab.allPriorities")}
                name="priorityFilter"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={
                  priorityFilter
                    ? {
                        value: priorityFilter,
                        label:
                          priorityFilter === "High"
                            ? t("flagTab.high")
                            : priorityFilter === "Medium"
                              ? t("flagTab.medium")
                              : priorityFilter === "Low"
                                ? t("flagTab.low")
                                : priorityFilter === "Informational"
                                  ? t("flagTab.informational")
                                  : priorityFilter,
                      }
                    : null
                }
                onChange={handlePriorityChange}
                options={[
                  { value: "", label: t("flagTab.allPriorities") },
                  { value: "High", label: t("flagTab.high") },
                  { value: "Medium", label: t("flagTab.medium") },
                  { value: "Low", label: t("flagTab.low") },
                  { value: "Informational", label: t("flagTab.informational") },
                ]}
                isClearable
              />
            </FormGroup>
          </div>
          <div
            className={classNames(
              compact ? "col-12 col-md-3 px-0" : "col-md-4 pr-0",
              "d-flex justify-content-md-end align-items-end"
            )}
          >
            {permission?.inventory_flags_add && (
              <Button
                onClick={handleAddFlag}
                style={{ ...activeColorStyle, color: "white" }}
                className={compact ? "w-100" : "ml-3"}
              >
                <i className="tim-icons icon-simple-add mr-2" />
                {t("flagTab.addFlag")}
              </Button>
            )}
          </div>
        </div>

        {!showAddForm && (
          <div className="mb-2">
            <p className="mb-0 text-muted" style={{ fontSize: "0.875rem" }}>
              {isLoading
                ? t("flags.loading")
                : `${totalFlags} ${t("flagTab.flagsAvailable")}`}
            </p>
          </div>
        )}

        {/* Flags List - Only show when not adding a new flag */}
        {!showAddForm && (
          <div className="row mx-0">
            <div className="col-12 px-0 flag-tab-list">
              {pagedFlags?.length > 0 ? (
                <Row className="mx-0">
                  {pagedFlags?.map((flag) => (
                    <Col
                      key={flag.id || flag.documentId}
                      lg={compact ? 12 : 6}
                      md={compact ? 12 : 6}
                      sm={12}
                      className={classNames("mb-2", { "px-0": compact, "px-1": !compact })}
                    >
                      <Card
                        className="flag-card"
                        style={{
                          transition: compact ? undefined : "all 0.3s ease",
                          cursor: "pointer",
                          overflow: "hidden",
                          boxShadow: compact ? "none" : "0 2px 4px rgba(0,0,0,0.1)",
                          border: compact
                            ? isLightTheme
                              ? "1px solid rgba(0,0,0,0.08)"
                              : "1px solid rgba(255,255,255,0.08)"
                            : undefined,
                          background: newSelectedFlags.includes(flag.id || flag.documentId)
                            ? isLightTheme
                              ? "rgba(47, 128, 237, 0.08)"
                              : "rgba(0, 146, 224, 0.15)"
                            : undefined,
                        }}
                        onMouseEnter={(e) => {
                          if (
                            !compact &&
                            !newSelectedFlags.includes(flag.id || flag.documentId)
                          ) {
                            e.currentTarget.style.transform = cardHoverStyle.transform;
                            e.currentTarget.style.boxShadow = cardHoverStyle.boxShadow;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (
                            !compact &&
                            !newSelectedFlags.includes(flag.id || flag.documentId)
                          ) {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                          }
                        }}
                        onClick={() => {
                          if (permission?.inventory_flags_add) {
                            handleFlagToggle(flag.id || flag.documentId);
                          }
                        }}
                      >
                        <CardBody className="d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center flex-grow-1 min-w-0">
                              {permission?.inventory_flags_add && (
                                <div
                                  style={{
                                    width: 20,
                                    height: 20,
                                    marginRight: 12,
                                    position: "relative",
                                    background: "transparent",
                                    flexShrink: 0,
                                  }}
                                >
                                  <FormGroup check className="mt-0 mb-0" style={{ height: "100%" }}>
                                    <Label
                                      check
                                      className="mb-0"
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        padding: 0,
                                        margin: 0,
                                        width: "100%",
                                        height: "100%",
                                        display: "block",
                                      }}
                                    >
                                      <Input
                                        type="checkbox"
                                        className="form-check-input"
                                        style={{
                                          position: "absolute",
                                          opacity: 0,
                                          width: "100%",
                                          height: "100%",
                                          top: 0,
                                          left: 0,
                                          margin: 0,
                                        }}
                                        checked={newSelectedFlags.includes(
                                          flag.id || flag.documentId
                                        )}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleFlagToggle(flag.id || flag.documentId);
                                        }}
                                      />
                                      <span className="form-check-sign" />
                                    </Label>
                                  </FormGroup>
                                </div>
                              )}
                              <div className="flex-grow-1 min-w-0">
                                <h6
                                  className="mb-1 font-weight-bold"
                                  style={{
                                    fontSize: "0.8rem",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {flag.name}
                                </h6>
                                <p
                                  className="text-muted mb-0"
                                  style={{
                                    fontSize: "0.8rem",
                                    lineHeight: "1.35",
                                    display: "-webkit-box",
                                    WebkitLineClamp: compact ? 2 : 3,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {flag.description}
                                </p>
                              </div>
                            </div>
                            <div className="ml-2" style={{ flexShrink: 0 }}>
                              <Badge
                                color={getPriorityColor(flag.priority)}
                                style={{
                                  fontSize: "0.7rem",
                                  padding: "0.35rem 0.55rem",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <i
                                  className={`tim-icons mr-1 ${
                                    flag.priority === "High"
                                      ? "icon-alert-circle-exc"
                                      : flag.priority === "Medium"
                                        ? "icon-bell-55"
                                        : flag.priority === "Low"
                                          ? "icon-chart-pie-36"
                                          : "icon-info"
                                  }`}
                                />
                                {flag.priority}
                              </Badge>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Alert color="info" className="text-center mb-0">
                  <i className="tim-icons icon-bell-55 mr-2" />
                  {isLoading
                    ? t("flags.loading")
                    : priorityFilter
                      ? t("flagTab.noFlagsFoundWithPriority", { priority: priorityFilter })
                      : debouncedSearch
                        ? t("flagTab.noFlagsFoundForSearch")
                        : t("flagTab.noFlagsFound")}
                </Alert>
              )}

              {!showAddForm && (pageCount > 1 || (pagedFlags.length > 0 && permission?.inventory_flags_add)) && (
                <div
                  className={classNames("flag-tab-footer", {
                    "flag-tab-footer--light": isLightTheme,
                  })}
                >
                  {pageCount > 1 && (
                    <div
                      className="d-flex align-items-center justify-content-between flex-wrap mb-3"
                      style={{ gap: 10 }}
                    >
                      <span className="text-muted mb-0" style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                        {t("flagTab.pageOf", { page: safePage, pageCount })}
                      </span>
                      <Pagination
                        size="sm"
                        className={classNames("flag-tab-pagination mb-0", {
                          "flag-tab-pagination--light": isLightTheme,
                          "flag-tab-pagination--dark": !isLightTheme,
                        })}
                        listClassName="mb-0"
                      >
                        <PaginationItem disabled={safePage <= 1}>
                          <PaginationLink
                            previous
                            href="#prev"
                            onClick={(e) => {
                              e.preventDefault();
                              if (safePage > 1) handlePageChange(safePage - 1);
                            }}
                          />
                        </PaginationItem>
                        {paginationItems.map((item) =>
                          item.type === "ellipsis" ? (
                            <PaginationItem key={item.key} disabled>
                              <PaginationLink href="#ellipsis" onClick={(e) => e.preventDefault()}>
                                …
                              </PaginationLink>
                            </PaginationItem>
                          ) : (
                            <PaginationItem key={item.page} active={item.page === safePage}>
                              <PaginationLink
                                href={`#page-${item.page}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePageChange(item.page);
                                }}
                                style={
                                  item.page === safePage
                                    ? { ...activeColorStyle, color: "#fff", border: 0 }
                                    : undefined
                                }
                              >
                                {item.page}
                              </PaginationLink>
                            </PaginationItem>
                          )
                        )}
                        <PaginationItem disabled={safePage >= pageCount}>
                          <PaginationLink
                            next
                            href="#next"
                            onClick={(e) => {
                              e.preventDefault();
                              if (safePage < pageCount) handlePageChange(safePage + 1);
                            }}
                          />
                        </PaginationItem>
                      </Pagination>
                    </div>
                  )}

                  {pagedFlags.length > 0 && permission?.inventory_flags_add && (
                    <div
                      className="d-flex align-items-center"
                      style={{ gap: 8 }}
                    >
                      <Button
                        color="secondary"
                        className="mb-0 flex-fill"
                        onClick={() => setNewSelectedFlags([])}
                      >
                        <i className="tim-icons icon-simple-remove mr-1" />
                        {t("flagTab.clearSelection")}
                      </Button>
                      <Button
                        style={{ ...activeColorStyle, color: "white" }}
                        className="flex-fill"
                        onClick={handleAssignFlags}
                        disabled={assignLoading}
                      >
                        <i className="tim-icons icon-check-2 mr-2" />
                        {assignLoading
                          ? t("flagTab.assigning")
                          : t("flagTab.assignSelectedFlags")}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {assignError && (
                <Alert color="danger" className="mt-3">
                  {assignError}
                </Alert>
              )}
            </div>
          </div>
        )}

        {/* Add Flag Form */}
        {showAddForm && (
          <Card className="mt-3">
            <CardHeader>
              <div className="d-flex justify-content-between align-items-center">
                <h4 style={{ margin: 0 }}>
                  <i className="tim-icons icon-simple-add mr-2" />
                  {t("flagTab.addNewFlag")}
                </h4>
                <Button color="secondary" size="sm" onClick={handleCancelAdd}>
                  <i className="tim-icons icon-simple-remove mr-1" />
                  {t("flagTab.cancel")}
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <Form onSubmit={handleSubmitForm}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("flagTab.flagName")}</Label>
                      <Input
                        type="text"
                        className={isTheme2 ? "form-control" : undefined}
                        style={inputStyle}
                        value={form.name}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, name: e.target.value }))
                        }
                        placeholder={t("flagTab.enterFlagName")}
                        invalid={!!formErrors.name}
                      />
                      {formErrors.name && (
                        <div className="invalid-feedback d-block">{formErrors.name}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("flagTab.priority")}</Label>
                      <Select
                        activeColor={activeColor}
                        styles={selectStyles}
                        className="react-select"
                        placeholder="Select Priority"
                        name="priority"
                        isSearchable={false}
                        closeMenuOnSelect={true}
                        value={
                          getPriorityOptions(t).find((opt) => opt.value === form.priority) ||
                          null
                        }
                        onChange={(option) =>
                          setForm((prev) => ({
                            ...prev,
                            priority: option?.value || "",
                          }))
                        }
                        options={getPriorityOptions(t)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label>{t("flagTab.description")}</Label>
                  <Input
                    type="textarea"
                    className={isTheme2 ? "form-control" : undefined}
                    style={inputStyle}
                    rows={4}
                    value={form.description}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, description: e.target.value }))
                    }
                    placeholder={t("flagTab.enterDescription")}
                  />
                </FormGroup>
                <div className="d-flex justify-content-end mt-4">
                  <Button
                    type="submit"
                    style={{ ...activeColorStyle, color: "white" }}
                    size="lg"
                    disabled={createFlagMutation.isPending}
                  >
                    <i className="tim-icons icon-check-2 mr-2" />
                    {createFlagMutation.isPending
                      ? t("flagTab.creating")
                      : t("flagTab.createFlag")}
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FlagTab;
